
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function CheckoutPage() {
  const [quantity, setQuantity] = useState(1);
  const [size, setSize] = useState('M');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const pricePerItem = 30;
  const total = pricePerItem * quantity;

  const sizes = ['S', 'M', 'L', 'XL', '2XL'];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });
    data.append('product', 'Tha Realest Black T-Shirt');
    data.append('size', size);
    data.append('quantity', quantity.toString());
    data.append('total', `$${total}`);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6266vg0h9ov63emh20g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-green-500/20 rounded-full">
            <i className="ri-check-line text-4xl text-green-500"></i>
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Order Submitted!</h1>
          <p className="text-gray-400 mb-8">Thank you for your order. We'll contact you shortly with payment and shipping details.</p>
          <Link 
            to="/" 
            className="inline-block px-8 py-3 bg-[#8B0000] text-white font-bold uppercase tracking-wider rounded-full hover:bg-[#6B0000] transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black">
      {/* Header */}
      <header className="bg-black/95 backdrop-blur-sm fixed top-0 left-0 right-0 z-50">
        <div className="px-6 lg:px-20 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-white hover:text-cyan-400 transition-colors">
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-sm font-medium tracking-wider">BACK TO SITE</span>
          </Link>
          <span className="text-white text-sm font-bold tracking-wider uppercase">Checkout</span>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 lg:px-20">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl lg:text-4xl font-bold text-white text-center mb-12">Complete Your Order</h1>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Summary */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 lg:p-8 border border-white/10">
              <h2 className="text-xl font-bold text-white mb-6 uppercase tracking-wider">Order Summary</h2>
              
              <div className="flex gap-6 mb-8">
                <div className="w-32 h-32 bg-white rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src="https://static.readdy.ai/image/65bc6cd4bf89e9871e78f57376195ee1/152df9dca1e1a7ce2a3dd0a9e36f1733.png"
                    alt="Tha Realest Black T-Shirt"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-2">Tha Realest T-Shirt</h3>
                  <p className="text-gray-400 text-sm mb-4">Official Black Logo Tee</p>
                  <p className="text-2xl font-bold text-white">${pricePerItem}</p>
                </div>
              </div>

              {/* Size Selection */}
              <div className="mb-6">
                <label className="block text-white text-sm font-medium mb-3 uppercase tracking-wider">Select Size</label>
                <div className="flex gap-3">
                  {sizes.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSize(s)}
                      className={`w-12 h-12 rounded-lg font-bold text-sm transition-all cursor-pointer ${
                        size === s 
                          ? 'bg-[#8B0000] text-white' 
                          : 'bg-white/10 text-gray-400 hover:bg-white/20'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-8">
                <label className="block text-white text-sm font-medium mb-3 uppercase tracking-wider">Quantity</label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="text-white text-xl font-bold w-8 text-center">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Total */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-lg">Total</span>
                  <span className="text-3xl font-bold text-white">${total}</span>
                </div>
              </div>
            </div>

            {/* Checkout Form */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 lg:p-8 border border-white/10">
              <h2 className="text-xl font-bold text-white mb-6 uppercase tracking-wider">Shipping Details</h2>
              
              <form 
                id="checkout-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Street Address</label>
                  <input
                    type="text"
                    name="address"
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors"
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">City</label>
                    <input
                      type="text"
                      name="city"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors"
                      placeholder="Los Angeles"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">State</label>
                    <input
                      type="text"
                      name="state"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors"
                      placeholder="CA"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">ZIP Code</label>
                    <input
                      type="text"
                      name="zipCode"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors"
                      placeholder="90001"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Country</label>
                    <input
                      type="text"
                      name="country"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors"
                      placeholder="United States"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Order Notes (Optional)</label>
                  <textarea
                    name="notes"
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm outline-none focus:border-[#8B0000] transition-colors resize-none"
                    placeholder="Any special instructions..."
                  />
                </div>

                {error && (
                  <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#8B0000] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#6B0000] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Processing...' : `Place Order - $${total}`}
                </button>

                <p className="text-gray-500 text-xs text-center">
                  By placing your order, you agree to our terms and conditions. Payment details will be sent via email.
                </p>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6 px-6 text-center">
        <p className="text-gray-500 text-xs">© {new Date().getFullYear()} Tha Realest. All Rights Reserved.</p>
      </footer>
    </div>
  );
}
